import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";

import Header from '../../Component/Header.js';
import Menu from '../../Component/Menu.js';
import { currentTenant, userid } from '../../Common/Credentials.js';
import { spin } from '../../Common/NewLoader.jsx';
import { appmasterid } from '../../Common/Permissions.js';
import WarehouseServices from './WarehouseServices.js';
import EditIcon from "../../images/Icons/edit.png";
import DeleteIcon from "../../images/Icons/delete.png";
import Breadcrumb from '../../NewUI/NewComponents/Breadcrumb.jsx';

const WarehouseTable = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshWarehouses = useCallback(() => {
    setLoading(true);

    WarehouseServices.getWarehouses(currentTenant)
      .then((response) => {
        const sortedData = response.data.sort((a, b) => a.name?.localeCompare(b.name));
        setWarehouses(sortedData);
        setFilteredData(sortedData);
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          Swal.fire({
            title: "Your Session is Lost: Please login Back to resume",
            text: ".",
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#152f5f",
          }).then(() => navigate("/"));
        }
        setLoading(false);
      });
  }, [currentTenant, navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    refreshWarehouses();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshWarehouses]);

  useEffect(() => {
    updateFilteredData();
  }, [warehouses, searchTerm]);

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'address':
          aValue = a.address || '';
          bValue = b.address || '';
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = warehouses;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = warehouses.filter(warehouse =>
        warehouse.name?.toLowerCase().includes(lowerSearchTerm) ||
        warehouse.address?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredData);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const deleteWarehouse = (id, name) => {
    Swal.fire({
      title: `Delete ${name}?`,
      text: "Are you sure you want to delete this organization unit?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#152f5f",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.value) {
        WarehouseServices.deleteWarehouse(id)
          .then(() => {
            Swal.fire({
              title: "Organization unit deleted successfully",
              text: ".",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => {
              setLoading(true);
              refreshWarehouses();
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            } else {
              Swal.fire({
                title: "Error occurred while deleting",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              });
            }
          });
      }
    });
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredData.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} organization units
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredData.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No organization units found matching your search' : 'No organization units available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'name' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('name')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Organization Unit
                    <SortIndicator columnKey="name" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'address' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('address')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Address
                    <SortIndicator columnKey="address" />
                  </div>
                </th>
                {(appmasterid === "2" || appmasterid === "3") && (
                  <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((warehouse) => (
                <tr
                  key={warehouse.id}
                  className={`UI-table-row ${hoveredRow === warehouse.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                  onMouseEnter={() => setHoveredRow(warehouse.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  {/* Organization Unit Name */}
                  <td
                    className={`UI-td UI-service-name ${hoveredRow === warehouse.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(warehouse.name, searchTerm)}
                  </td>

                  {/* Address */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(warehouse.address, searchTerm)}
                  </td>

                  {/* Actions Cell */}
                  {(appmasterid === "2" || appmasterid === "3") && (
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <div className="UI-action-buttons !gap-0">
                        {/* Edit Warehouse */}
                        <Tooltip title="Edit Organization Unit" arrow slotProps={{
                          popper: {
                            sx: {
                              pointerEvents: 'none',
                            },
                          },
                        }}>
                          <button
                            onClick={() => navigate("/newwarehouse", {
                              state: {
                                id: warehouse.id,
                                pagename: 'Edit Org Unit',
                              },
                            })}
                            className={`UI-action-button  ${hoveredAction === `edit-${warehouse.id}` ? 'UI-edit-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                            onMouseEnter={() => setHoveredAction(`edit-${warehouse.id}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                          >
                            <img
                              src={EditIcon}
                              alt="Edit"
                              className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `edit-${warehouse.id}` ? 'UI-action-icon-hover' : ''}`}
                              style={{ width: "20px", height: "20px" }}
                            />
                          </button>
                        </Tooltip>

                        {/* Delete Warehouse */}
                        {warehouses.length > 1 && (
                          <Tooltip title="Delete Organization Unit" arrow slotProps={{
                            popper: {
                              sx: {
                                pointerEvents: 'none',
                              },
                            },
                          }}>
                            <button
                              onClick={() => deleteWarehouse(warehouse.id, warehouse.name)}
                              className={`UI-action-button  ${hoveredAction === `delete-${warehouse.id}` ? 'UI-delete-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                              onMouseEnter={() => setHoveredAction(`delete-${warehouse.id}`)}
                              onMouseLeave={() => setHoveredAction(null)}
                            >
                              <img
                                src={DeleteIcon}
                                alt="Delete"
                                className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `delete-${warehouse.id}` ? 'UI-action-icon-hover' : ''}`}
                                style={{ width: "20px", height: "20px" }}
                              />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className="">
          <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
            <div className="UI-card">
              {/* Header */}
              <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
              <Breadcrumb breadcrumbs={[{name:"Home",path:"/masters"},{name:"Organization Units",path:null}]}/>

                <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
                  <div>
                    <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                      Organization Units
                    </h1>
                    <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                      Manage organization unit records
                    </p>
                  </div>

                  <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
                    {/* Mobile Search */}
                    {isMobile && (
                      <input
                        type="text"
                        placeholder="Search organization units..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="UI-mobile-search-input"
                      />
                    )}

                    <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                      {/* Desktop Search Toggle */}
                      {!isMobile && (
                        <div
                          className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                          onClick={() => {
                            if (!showSearch) setShowSearch(true);
                          }}
                          onMouseEnter={() => setHovered("search")}
                          onMouseLeave={() => setHovered(null)}
                        >
                          <i
                            className="fa fa-search UI-search-icon"
                          />
                          {!showSearch && "Search"}
                          {showSearch && (
                            <input
                              type="text"
                              placeholder="Search organization units..."
                              value={searchTerm}
                              onChange={(e) => handleSearch(e.target.value)}
                              className="UI-search-input"
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                              onBlur={() => {
                                if (searchTerm.trim() === "") {
                                  setShowSearch(false);
                                }
                              }}
                            />
                          )}
                        </div>
                      )}

                      {/* New Warehouse button */}
                      {(appmasterid === "2" || appmasterid === "3") && (
                        <Link
                          to="/newwarehouse"
                          state={{ id: -1, pagename: 'New Org Unit' }}
                          className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                          onMouseEnter={() => setHovered("new")}
                          onMouseLeave={() => setHovered(null)}
                        >
                          <i className="fa fa-plus" /> {isMobile ? "New" : "New Org Unit"}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="UI-loading-container">
                  {spin}
                </div>
              ) : (
                renderTable()
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WarehouseTable;